import {
  IDynamicBindableValue,
  IDynamicEventHandler,
  IDynamicItem,
  IDynamicItemStyles,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import {
  errorMessage,
  ifHasNoErrorAndIsNotLoading,
  ifIsLoading,
  referenceloadingStateWrapper,
} from '../../loading-state-wrapper-helpers';
import { conditionalMessage } from '../../misc';
import { recommendationsPanelsLocaleKeys } from '@epicuro-next/constants/features/localization';
import { recommendationItemShownEventHandler } from '../../events';

const truncateStyle =
  'text-wrap: nowrap; overflow: hidden; text-overflow: ellipsis;';
const panelWidth = '187px';
const defaultDoneLoadingWrapperStyles = {
  classes: ['d-block', 'panel-3-items-with-no-media-3', 'search-bar-block'],
  style: {
    margin: '0.5rem 0.3rem',
    'border-radius': 'var(--border-radius-lg, .25rem)',
    background:
      'linear-gradient(to top, rgb(255 255 255) 0%, rgb(255, 255, 255) 52%, rgb(223 223 223) 100%)',
    border: '1px solid #999999',
    padding: '0 10px',
    'max-height': '250px',
    height: '250px',
    width: panelWidth,
  },
  css: '.search-bar-block .price-column {margin: 0 0.5rem 0.25rem 0; align-items: start;}',
};

const loadingSkeleton = {
  id: 'loading-skeleton',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['h-100'],
  },
  inputs: {
    media: {
      url: 'assets/static-skeleton-screen.png',
    },
    objectFit: 'cover',
  },
};

export const errorMessageItem = {
  ...errorMessage(),
  styles: {
    classes: [
      'd-flex',
      'w-100',
      'justify-content-center',
      'mb-2',
      'text-muted',
    ],
  },
};

export const noItemsItem: IDynamicItem = conditionalMessage(
  'no-items-message',
  'parent',
  'hasNoItems',
  recommendationsPanelsLocaleKeys.noRecommendedItemsReturned,
  {
    classes: [
      'd-flex',
      'w-100',
      'justify-content-center',
      'text-muted',
      'mb-2',
    ],
  },
);

export const backgroundMedia = {
  id: 'background-media',
  contentTypeId: 'dynamic-elements-media-preview',
  styles: {
    classes: ['marketing-panel__figure'],
    style: {
      'border-radius': 'var(--border-radius-lg, .25rem)',
    },
  },
  inputs: {
    media: {
      url: 'https://epicuropumatestimages.epicurotesting.com/images/static/promotions/1b59f5caa20540e6b6796c4ea8387e4b.png?sv=2019-07-07&sr=b&sig=S1CZo2heZlQHjL8xOGDGoRqeFaRLpeP7VMQq7AfNAB8%3D&se=2123-03-27T12%3A18%3A48Z&sp=r&width=1920&mode=max',
    },
    objectFit: 'cover',
  },
};

export function getItemsArrayRenderer(contentType: IDynamicItem) {
  return {
    id: 'items-array-renderer',
    contentTypeId: 'dynamic-elements-array-renderer',
    if: ifHasNoErrorAndIsNotLoading(),
    styles: {
      classes: ['marketing-panel__items', 'justify-content-center'],
      style: {
        'border-radius': 'var(--border-radius-lg, .25rem)',
      },
    },
    data: {
      products: {
        provider: 'parent',
        property: 'productData',
        transform: function (productDataArg: []) {
          return (productDataArg ?? []).slice(0, 1);
        }.toString(),
      },
      loadingStateWrapper: referenceloadingStateWrapper('parent'),
    },
    inputs: {
      propertyToUse: 'productData',
      items$: {
        provider: 'self',
        property: 'products',
      },
      contentType,
    },
  };
}

export function getIsLoadingWrapper() {
  return {
    id: 'is-loading-wrapper',
    contentTypeId: 'dynamic-collections-renderer',
    styles: {
      style: {
        width: '200px',
        height: '260px',
        margin: '0.3rem 0.5rem',
        display: 'block',
      },
    },
    if: ifIsLoading(),
    children: [
      {
        contentTypeId: 'dynamic-collections-renderer',
        children: [loadingSkeleton],
      },
    ],
  };
}

export function getBackgroundMedia(): IDynamicItem {
  return {
    id: 'background-media',
    contentTypeId: 'dynamic-elements-media-preview',
    styles: {
      classes: ['marketing-panel__figure'],
      style: {
        'border-radius': 'var(--border-radius-lg, .25rem)',
      },
    },
    inputs: {
      media: {
        url: 'https://epicuropumatestimages.epicurotesting.com/images/static/promotions/1b59f5caa20540e6b6796c4ea8387e4b.png?sv=2019-07-07&sr=b&sig=S1CZo2heZlQHjL8xOGDGoRqeFaRLpeP7VMQq7AfNAB8%3D&se=2123-03-27T12%3A18%3A48Z&sp=r&width=1920&mode=max',
      },
      objectFit: 'cover',
    },
  };
}

export function getIsDoneLoadingWrapper(
  children: IDynamicItem[],
  styles: Partial<IDynamicItemStyles> = defaultDoneLoadingWrapperStyles,
): IDynamicItem {
  return {
    id: 'is-done-loading-wrapper',
    contentTypeId: 'dynamic-collections-renderer',
    styles,
    if: [
      'EQ',
      [
        false,
        {
          provider: 'parent',
          property: 'isLoading',
        },
      ],
    ],
    children,
  };
}

export function getSearchBarPanelTitle(
  id: string,
  textProvider: IDynamicBindableValue,
): IDynamicSimpleText {
  return {
    id,
    contentTypeId: 'dynamic-elements-simple-text',
    styles: {
      style: {},
      variables: {
        '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
        '--text-dynamic-font-weight': '700',
      },
      classes: [
        'search-bar-block-list-name',
        'd-flex',
        'justify-content-center',
      ],
      css: `
          .search-bar-block-list-name {font-size: 1rem; padding: 3px;}
          .search-bar-block-list-name .text-value {${truncateStyle}}
          `,
    },
    inputs: {
      text: textProvider,
    },
  };
}

export function getSearchBarPanelSubTitle(
  id: string,
  textProvider: IDynamicBindableValue,
): IDynamicSimpleText {
  return {
    id,
    contentTypeId: 'dynamic-elements-simple-text',
    if: [
      'EQ',
      [
        false,
        {
          provider: 'id:root',
          property: 'hasNoItems',
        },
      ],
    ],
    styles: {
      variables: {
        '--text-dynamic-font-size': '0.75rem',
      },
      classes: [
        'search-bar-block-product-name',
        'd-flex',
        'justify-content-center',
      ],
      css: `
          .search-bar-block-product-name {margin: 3px 0;}
          .search-bar-block-product-name .text-value {${truncateStyle}}
          `,
    },
    inputs: {
      text: textProvider,
    },
  };
}

export function getItemMediaPreview(
  panelNameProvider: IDynamicBindableValue,
  panelStyleProvider: IDynamicBindableValue,
  styles: Partial<IDynamicItemStyles>,
  onClickHandlers: IDynamicEventHandler | IDynamicEventHandler[],
): IDynamicItem {
  return {
    id: 'item-media-preview',
    contentTypeId: 'dynamic-elements-media-preview',
    styles,
    inputs: {
      media: {
        provider: 'self',
        property: 'productData.media',
      },
    },
    eventHandlers: {
      onClick: onClickHandlers,
      onVisibleOnce: [
        recommendationItemShownEventHandler(
          panelNameProvider,
          panelStyleProvider,
          {
            provider: 'self',
            property: 'productData',
          },
        ),
      ],
    },
  };
}
